/**
 * State/region-level theft stats used by the geo-aware TheftUrgency headline.
 * Keys are normalised ISO-3166-2 subdivision codes OR ip-api's `region` string
 * uppercased (ip-api returns e.g. "QUEENSLAND" for AU — we normalise both).
 *
 * AU figures: Australian Bureau of Statistics "Recorded Crime — Victims" 2024–25.
 * US figures: FBI UCR + NICB 2024 state totals (rounded to make copy readable).
 * UK figures: ONS crime stats + Home Office 2024 vehicle theft tables.
 *
 * Numbers are deliberately rounded to the nearest hundred/thousand so the
 * headline reads naturally. We never claim precision we don't have.
 */

// Keys accept both ISO code (QLD) and ip-api full name (QUEENSLAND).
const AU_STATS = {
  QLD: { name: "Queensland",          stolen: 15800 },
  NSW: { name: "New South Wales",     stolen: 19100 },
  VIC: { name: "Victoria",            stolen: 17300 },
  SA:  { name: "South Australia",     stolen: 5900  },
  WA:  { name: "Western Australia",   stolen: 6400  },
  TAS: { name: "Tasmania",            stolen: 1050  },
  ACT: { name: "ACT",                 stolen: 1400  },
  NT:  { name: "Northern Territory",  stolen: 1600  },
};

const US_STATS = {
  CA: { name: "California", stolen: 177000 },
  TX: { name: "Texas",      stolen: 115000 },
  FL: { name: "Florida",    stolen: 44000  },
  WA: { name: "Washington", stolen: 36000  },
  CO: { name: "Colorado",   stolen: 32000  },
  AZ: { name: "Arizona",    stolen: 24000  },
  GA: { name: "Georgia",    stolen: 23000  },
  OR: { name: "Oregon",     stolen: 22000  },
  NV: { name: "Nevada",     stolen: 18000  },
  OH: { name: "Ohio",       stolen: 17000  },
  IL: { name: "Illinois",   stolen: 16000  },
  NY: { name: "New York",   stolen: 15000  },
  NC: { name: "N. Carolina",stolen: 13000  },
  PA: { name: "Pennsylvania",stolen: 10000 },
  MI: { name: "Michigan",   stolen: 10000  },
  TN: { name: "Tennessee",  stolen: 10000  },
  MO: { name: "Missouri",   stolen: 11000  },
  IN: { name: "Indiana",    stolen: 10000  },
  MA: { name: "Massachusetts",stolen: 5000 },
  MD: { name: "Maryland",   stolen: 8000   },
  VA: { name: "Virginia",   stolen: 7000   },
  NJ: { name: "New Jersey", stolen: 7000   },
  WI: { name: "Wisconsin",  stolen: 5500   },
  AL: { name: "Alabama",    stolen: 9000   },
  OK: { name: "Oklahoma",   stolen: 11000  },
};

const GB_STATS = {
  ENG: { name: "England",          stolen: 112000 },
  WLS: { name: "Wales",            stolen: 4700   },
  SCT: { name: "Scotland",         stolen: 8800   },
  NIR: { name: "Northern Ireland", stolen: 2300   },
};

// Full-name aliases ip-api sometimes returns.
const ALIASES_AU = {
  QUEENSLAND: "QLD", "NEW SOUTH WALES": "NSW", VICTORIA: "VIC",
  "SOUTH AUSTRALIA": "SA", "WESTERN AUSTRALIA": "WA", TASMANIA: "TAS",
  "AUSTRALIAN CAPITAL TERRITORY": "ACT", "NORTHERN TERRITORY": "NT",
};
const ALIASES_GB = {
  ENGLAND: "ENG", WALES: "WLS", SCOTLAND: "SCT", "NORTHERN IRELAND": "NIR",
};

const normaliseRegion = (country, region) => {
  if (!region) return null;
  const key = region.toUpperCase().trim();
  if (country === "AU") return ALIASES_AU[key] || key;
  if (country === "GB") return ALIASES_GB[key] || key;
  return key; // US already ISO (CA, TX, …)
};

/**
 * @param {"AU"|"US"|"GB"|null} country
 * @param {string|null} region
 * @returns {{ name: string, stolen: number } | null} — null signals "no geo match, use national fallback"
 */
export function regionalTheftFor(country, region) {
  if (!country || !region) return null;
  const key = normaliseRegion(country, region);
  if (!key) return null;
  if (country === "AU") return AU_STATS[key] || null;
  if (country === "US") return US_STATS[key] || null;
  if (country === "GB") return GB_STATS[key] || null;
  return null;
}

/** Compact human-formatted number (e.g. 15800 → "15,800"). */
export function formatStolen(n) {
  return n.toLocaleString("en-US");
}
